import { CreditHistory } from '@/models/simulation/CreditHistory';
import { LoanPurpose } from '@/models/simulation/LoanPurpose';
import { FFHouseholdDTO } from '@/models/simulation/FFHouseholdDTO';
import { MarketType } from '@/models/simulation/MarketType';
import { LoanPurposeActivity } from '@/models/simulation/LoanPurposeActivity';
import { LoanPurposeWhat } from '@/models/simulation/LoanPurposeWhat';
import { LoanPurposeHow } from '@/models/simulation/LoanPurposeHow';
import { BankSymbol } from '@/commons/dictionaries/BankSymbol';
import { Utils } from '@/services/utils/BasicUtils';
import { OfferDataTableElementKey } from '@/components/calculator/results/offers/OfferTableElement';
import { FilterKey, OfferFilter } from '@/components/calculator/results/OfferSorting';
import { Currency } from '@/modules/multiForm/shared/MultiformDictionaries';
import EnvironmentService from '@/env/EnvironmentService';
import { FirstOrSubsequentRealEstate } from '@/models/simulation/FirstOrSubsequentRealEstate';
import { InsuranceKind } from './simulation/subtypes';

export class SimulationDemandDTO {
  loanAmount: Nullable<number> = null;
  loanPeriodInMonths: Nullable<number> = null;
  loanPeriod: Nullable<number> = null;
  hypothecValue: Nullable<number> = null;
  fixedInstallments: boolean = true;
  constructionPeriod: Nullable<number> = null;
  currency: Currency = EnvironmentService.Environment.currentCurrency();
  purposeNotAsCollateral: boolean = false;
  additionalRequests: AdditionalRequests = {
    creditAllInitialCosts: false,
    requiredInsurances: {
      realEstateInsurance: EnvironmentService.Environment.getAppDomainConfig().simulationFilters.realEstateInsurance,
      lifeInsurance: EnvironmentService.Environment.getAppDomainConfig().simulationFilters.lifeInsurance,
    },
    referenceRate: null,
    referenceRateWibor3m: null,
    referenceRateWibor6m: null,
    referenceRateWibor1m: null,
    referenceRatePolstr1m: null,
    referenceRatePolstr3m: null,
    referenceRatePolstr6m: null,
    referenceRateEuribor1m: null,
    referenceRateEuribor2m: null,
    referenceRateEuribor3m: null,
    referenceRateEuribor6m: null,
    referenceRateEuribor12m: null,
    creditCardDemands: [],
    replacements: new Replacements(),
    acquireCreditCard: EnvironmentService.Environment.getAppDomainConfig().simulationFilters.acquireCreditCard,
    bankAccountDemands: [],
  };

  creditHistory: CreditHistory = { exists: false, };
  loanPurpose: LoanPurpose = {
    activity: LoanPurposeActivity.BUYING,
    what: LoanPurposeWhat.APARTMENT,
    market: MarketType.SECONDARY_MARKET,
    how: LoanPurposeHow.ON_YOUR_OWN,
  };

  firstOrSubsequentRealEstate: Nullable<FirstOrSubsequentRealEstate> =
    EnvironmentService.Environment.getAppDomainConfig().calculator.hasFirstOrSubsequentRealEstateInfo
      ? FirstOrSubsequentRealEstate.FIRST : null;

  households?: FFHouseholdDTO[] = [];
  acceptableDtiPercentage: Nullable<number> = null;
  organizationDomain: string = 'lendi.pl';
  stagingDBMode: boolean = false;
}

export const MORTGAGE_DEFAULT_LOAN_PERIOD_IN_YEARS = 30;
export const MORTGAGE_DEFAULT_LOAN_PERIOD_FOR_PURPOSE_LOAN_IN_YEARS = 20;
export const MORTGAGE_DEFAULT_CONSTRUCTION_PERIOD_IN_MONTHS = 24;

interface RefusedOffersUserInput {
  selectedBanks: BankSymbol[];
}

export class SimulationDemand extends SimulationDemandDTO {
  selectedBanks: BankSymbol[] = [];
  selectedOffers: string[] = [];
  pinnedOffers: string[] = [];
  collateral: LoanPurposeWhat | null = LoanPurposeWhat.APARTMENT;
  hideOfferName: boolean = false;
  showOnlySelected: boolean = false;
  isAscending: boolean = true;
  sortBy: OfferDataTableElementKey = OfferDataTableElementKey.TOTAL_SUM;
  offerFilters: Partial<OfferFilter> = {};
  refusedOffers: RefusedOffersUserInput = {
    selectedBanks: [],
  };

  homeStartProgramInput?: {
    numberOfAdult: Nullable<number>,
    numberOfChildren?: Nullable<number>,
    city: Nullable<string>
    propertyArea: Nullable<number>
    incomeSum: Nullable<number>
    isBuildingHouse: boolean
  } = {
      numberOfAdult: 1,
      numberOfChildren: 0,
      city: null,
      propertyArea: 50,
      incomeSum: 7000,
      isBuildingHouse: this.loanPurpose.what === LoanPurposeWhat.HOUSE,
    }

  acceptableDtiPercentage: Nullable<number> = EnvironmentService.Environment.isAppDomain().NESTO_RO ? 40 : null;

  stagingDBMode: boolean = false;

  constructor(dto?: Partial<SimulationDemandDTO>) {
    super();
    if (dto) {
      Utils.assign(this, dto);
    }
  }
}

// Offer filters travel inside the saved userInput, so simulations created before the EKO filter
// keys were renamed come back from the backend under the old key. An unknown key filters nothing
// yet still counts towards the active-filter badge, so map it onto its current equivalent instead
// of silently dropping the advisor's choice.
const LEGACY_OFFER_FILTER_KEYS: Record<string, FilterKey> = {
  notEKOTrue: 'isEkoFalse',
  notEKOFalse: 'isEkoTrue',
};

function migrateLegacyOfferFilters(offerFilters?: Partial<OfferFilter>): void {
  if (!offerFilters) {
    return;
  }
  for (const [legacyKey, currentKey] of Object.entries(LEGACY_OFFER_FILTER_KEYS)) {
    if (legacyKey in offerFilters) {
      offerFilters[currentKey] ??= offerFilters[legacyKey]!;
      delete offerFilters[legacyKey];
    }
  }
}

export class MortgageSimulationDemand extends SimulationDemand {
  hypothecValue: number = 800_000;
  explicitBridgingPeriod: number = 0;

  isLoanPeriodInMonths: boolean = true;
  loanPeriodInMonths: number = MORTGAGE_DEFAULT_LOAN_PERIOD_IN_YEARS * 12;
  offerFilters: Partial<OfferFilter> = EnvironmentService.Environment.isAppDomain().NESTO_RO
    ? { isEkoFalse: true, xSellTrue: true }
    : { noProgramTrue: true, xSellTrue: true };
  sortBy: OfferDataTableElementKey = EnvironmentService.Environment.isAppDomain().NESTO_RO
    ? OfferDataTableElementKey.TOTAL_SUM
    : OfferDataTableElementKey.FIRST_INSTALLMENT;

  constructor(dto?: Partial<MortgageSimulationDemand>) {
    super(dto);
    if (dto) {
      Utils.assign(this, dto);
      migrateLegacyOfferFilters(this.offerFilters);
      // 'Rata docelowa' left the PL sorting list - a simulation saved with it would come back
      // with a sort the select cannot show, so it falls back to the PL default.
      if (!EnvironmentService.Environment.isAppDomain().NESTO_RO && this.sortBy === OfferDataTableElementKey.LAST_INSTALLMENT) {
        this.sortBy = OfferDataTableElementKey.FIRST_INSTALLMENT;
      }
    }
  }
}

// Always sent exactly as the global switches show them; per-offer insuranceDemands override
// them offer by offer and neither side resets the other.
export interface RequiredInsurances {
  lifeInsurance?: boolean;
  realEstateInsurance?: boolean;
}

export interface IReplacment {
  loanId: string;
}

export interface LoanPeriodReplacement extends IReplacment {
  newLoanPeriodInMonths: number;
}

export interface MarginReplacement extends IReplacment {
  newInitialValue?: number;
  newEventualValue?: number;
  defaultInitialValue?: number;
  defaultEventualValue?: number;
}

export interface DefaultReplacement extends IReplacment {
  newValue: number;
}

export interface ProvisionReplacement extends IReplacment {
  newValue: number;
  defaultValue?: number;
}

export interface HypothecValueReplacement extends IReplacment {
  newValue: number;
}

export interface InsuranceReplacement {
  loanId?: string;
  insuranceVariantId: string;
  insuranceKind: InsuranceKind;
  insuranceId: string;
}

export type Replacement = LoanPeriodReplacement | MarginReplacement | DefaultReplacement | ProvisionReplacement | HypothecValueReplacement | InsuranceReplacement;

export interface InsuranceDemands {
  insuranceId: string;
  kind: InsuranceKind;
  loanId: string;
  required: boolean;
}

export enum ReplacementKind {
  MARGIN = 'marginReplacements',
  PROVISION = 'provisionReplacements',
  LOAN_AMOUNT = 'loanAmountReplacements',
  LOAN_PERIOD = 'loanPeriodReplacements',
  HYPOTHEC_VALUE = 'hypothecValueReplacements',
  BROKER_COMMISSION = 'brokerCommissionReplacements',
  INSURANCE = 'insuranceVariantReplacements',
}

// Infers the appropriate replacement type based on the provided ReplacementKind(enum).
// Returns a specific type based on the kind, such as DefaultReplacement, InsuranceReplacement, etc.
export type InferredReplacement<T extends ReplacementKind> = T extends infer R
  ? R extends (ReplacementKind.BROKER_COMMISSION | ReplacementKind.LOAN_AMOUNT) ? DefaultReplacement :
  R extends ReplacementKind.PROVISION ? ProvisionReplacement :
  R extends ReplacementKind.HYPOTHEC_VALUE ? HypothecValueReplacement :
  R extends ReplacementKind.INSURANCE ? InsuranceReplacement :
  R extends ReplacementKind.LOAN_PERIOD ? LoanPeriodReplacement :
  R extends ReplacementKind.MARGIN ? MarginReplacement : never : never

export class Replacements {
  [ReplacementKind.MARGIN]: MarginReplacement[] = [];
  [ReplacementKind.PROVISION]: ProvisionReplacement[] = [];
  [ReplacementKind.LOAN_AMOUNT]: DefaultReplacement[] = [];
  [ReplacementKind.LOAN_PERIOD]: LoanPeriodReplacement[] = [];
  [ReplacementKind.HYPOTHEC_VALUE]: HypothecValueReplacement[] = [];
  [ReplacementKind.BROKER_COMMISSION]: DefaultReplacement[] = [];
  [ReplacementKind.INSURANCE]: InsuranceReplacement[] = [];
}

export interface LoanVariant {
  originLoanId: string;
  variantLoanId: string;
}

export interface AdditionalProductDemand {
  acquired: boolean;
  loanId: string;
}

export interface ReferenceRate {
  referenceRate: Nullable<number>;
  referenceRateWibor1m: Nullable<number>;
  referenceRateWibor3m: Nullable<number>;
  referenceRateWibor6m: Nullable<number>;
  referenceRatePolstr1m: Nullable<number>;
  referenceRatePolstr3m: Nullable<number>;
  referenceRatePolstr6m: Nullable<number>;
  referenceRateEuribor1m: Nullable<number>;
  referenceRateEuribor2m: Nullable<number>;
  referenceRateEuribor3m: Nullable<number>;
  referenceRateEuribor6m: Nullable<number>;
  referenceRateEuribor12m: Nullable<number>;
}

export interface AdditionalRequests extends ReferenceRate {
  creditAllInitialCosts: boolean;
  creditCardDemands: AdditionalProductDemand[];
  bankAccountDemands: AdditionalProductDemand[];
  requiredInsurances: Nullable<RequiredInsurances>;
  replacements?: Replacements;
  insuranceDemands?: InsuranceDemands[];
  loanVariants?: LoanVariant[];
  // Same rule as requiredInsurances. acquireBankAccount has no global switch, so it stays
  // undefined (not sent) and bank accounts are driven by bankAccountDemands alone.
  acquireCreditCard?: boolean;
  acquireBankAccount?: boolean;
}

export class CashSimulationDemand extends SimulationDemand {
  customerProfile: string = 'SUPER';
  commissionLevel: string = 'L0';
  brokerCommission: Nullable<number> = null;
  minProvision: Nullable<number> = null;
  loanTypes = { consolidation: true, cash: true, };
  bankRelations: [] = [];
  loanPeriod: number = 60;
  offerFilters: Partial<OfferFilter> = {
    isEkoFalse: true,
  };

  additionalRequests: AdditionalRequests = {
    creditAllInitialCosts: true,
    requiredInsurances: {
      realEstateInsurance: true,
    },
    referenceRate: null,
    referenceRateWibor3m: null,
    referenceRateWibor6m: null,
    referenceRateWibor1m: null,
    referenceRatePolstr1m: null,
    referenceRatePolstr3m: null,
    referenceRatePolstr6m: null,
    referenceRateEuribor1m: null,
    referenceRateEuribor2m: null,
    referenceRateEuribor3m: null,
    referenceRateEuribor6m: null,
    referenceRateEuribor12m: null,
    creditCardDemands: [],
    replacements: new Replacements(),
    acquireCreditCard: false,
    bankAccountDemands: [],
  };

  constructor(dto?: Partial<CashSimulationDemand>) {
    super(dto);
    if (dto) {
      Utils.assign(this, dto);
      migrateLegacyOfferFilters(this.offerFilters);
    }
  }
}
